"use client";

import React, { useState, useMemo } from "react";
import { Search, Funnel, Ellipsis, Eye, Pencil, Trash2, Plus, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
function QuestionBanksTable() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    total,
    currentPage,
    totalPages
  } = usePosts();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const availableCategories = useMemo(() => {
    const cats = new Set<string>();
    posts.forEach(post => {
      if (post.customFields?.category_id) {
        cats.add(String(post.customFields.category_id));
      }
    });
    return Array.from(cats);
  }, [posts]);
  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === "all" || String(post.customFields?.category_id) === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [posts, searchTerm, selectedCategory]);
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-destructive mb-4 font-default text-lg">Failed to load property listings.</p>
        <Button variant="outline" onClick={() => window.location.reload()} className="rounded-md border-primary text-primary hover:bg-accent">Try Again</Button>
      </div>;
  }
  return <div className="space-y-6">
      {}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 items-center gap-4">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input placeholder="Search by city or zip..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 font-sans border-border/50 focus:ring-ring rounded-md" />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[180px] font-sans border-border/50 rounded-md">
              <Funnel className="mr-2 size-4 text-accent-foreground" />
              <SelectValue placeholder="Property Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              {availableCategories.map((cat, i) => <SelectItem key={i} value={cat} data-index={i}>
                  Type: {cat}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button className="font-default bg-primary text-primary-foreground hover:bg-primary/90 shadow-md rounded-md" onClick={() => window.location.href = "/ListYourPropertyPage"}>
          <Plus className="mr-2 size-4" />
          List Your Property
        </Button>
      </div>

      {}
      <Card className="overflow-hidden border-border/50 shadow-lg rounded-lg bg-card">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow className="border-border/50">
              <TableHead className="w-[300px] font-default font-bold text-foreground">
                <div className="flex items-center gap-2 cursor-pointer hover:text-accent-foreground transition-colors">
                  Property Address
                  <ArrowUpDown className="size-3" />
                </div>
              </TableHead>
              <TableHead className="font-default font-bold text-foreground">Down Payment</TableHead>
              <TableHead className="font-default font-bold text-foreground">Monthly</TableHead>
              <TableHead className="font-default font-bold text-foreground">Beds/Baths</TableHead>
              <TableHead className="font-default font-bold text-foreground">Parking</TableHead>
              <TableHead className="text-right font-default font-bold text-foreground">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({
            length: 5
          }).map((_, i) => <TableRow key={i} data-index={i} className="border-border/50">
                  <TableCell><div className="h-5 w-48 animate-pulse rounded bg-muted"></div></TableCell>
                  <TableCell><div className="h-5 w-16 animate-pulse rounded bg-muted"></div></TableCell>
                  <TableCell><div className="h-5 w-12 animate-pulse rounded bg-muted"></div></TableCell>
                  <TableCell><div className="h-5 w-24 animate-pulse rounded bg-muted"></div></TableCell>
                  <TableCell><div className="h-5 w-24 animate-pulse rounded bg-muted"></div></TableCell>
                  <TableCell><div className="h-8 w-8 animate-pulse rounded bg-muted ml-auto"></div></TableCell>
                </TableRow>) : filteredPosts.length === 0 ? <TableRow>
                <TableCell colSpan={6} className="h-32 text-center font-sans text-muted-foreground">
                  No properties found matching your search criteria.
                </TableCell>
              </TableRow> : filteredPosts.map((post, i) => <TableRow key={post.id} data-index={i} className="group border-border/50 hover:bg-accent/5 transition-colors">
                  <TableCell className="font-medium font-sans">
                    <Link to={post.link || "/FindHomePage"} className="text-foreground hover:text-accent-foreground transition-colors font-semibold">
                      {post.title || "Property Listing"}
                    </Link>
                    {post.customFields?.bank_description && <p className="text-muted-foreground mt-1 line-clamp-1 text-xs font-sans">
                        {post.customFields.bank_description}
                      </p>}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono font-medium border-accent-foreground/30 text-accent-foreground bg-accent/10 rounded-md">
                      {post.customFields?.difficulty_level ? `$${post.customFields.difficulty_level}` : "Contact Us"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-secondary font-semibold">
                    {post.customFields?.category_id ? `$${post.customFields.category_id}/mo` : "TBD"}
                  </TableCell>
                  <TableCell className="font-sans text-muted-foreground">
                    {post.customFields?.created_by || "3 Bed, 2 Bath"}
                  </TableCell>
                  <TableCell className="font-sans text-muted-foreground">
                    {post.date ? "Garage" : "Street Parking"}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100 hover:bg-accent/20">
                          <span className="sr-only">Open menu</span>
                          <Ellipsis className="size-4 text-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[160px] border-border/50 shadow-xl rounded-md">
                        <DropdownMenuLabel className="font-default text-xs uppercase tracking-wider text-muted-foreground">Options</DropdownMenuLabel>
                        <DropdownMenuItem asChild className="font-sans cursor-pointer focus:bg-accent/10">
                          <Link to={post.link || "/FindHomePage"}>
                            <Eye className="mr-2 size-4 text-accent-foreground" />
                            View Listing
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="font-sans cursor-pointer focus:bg-accent/10">
                          <Pencil className="mr-2 size-4 text-accent-foreground" />
                          Save Property
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-border/50" />
                        <DropdownMenuItem className="font-sans text-destructive focus:text-destructive cursor-pointer focus:bg-destructive/10">
                          <Trash2 className="mr-2 size-4" />
                          Hide Listing
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>)}
          </TableBody>
        </Table>
      </Card>

      {}
      {!loading && totalPages > 1 && <div className="flex items-center justify-between px-2">
          <p className="text-muted-foreground text-sm font-sans">
            Viewing page <span className="font-semibold text-foreground">{currentPage}</span> of{" "}
            <span className="font-semibold text-foreground">{totalPages}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="font-sans border-border/50 rounded-md hover:bg-accent/10">
              <ChevronLeft className="mr-1 size-4" />
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="font-sans border-border/50 rounded-md hover:bg-accent/10">
              Next
              <ChevronRight className="ml-1 size-4" />
            </Button>
          </div>
        </div>}
    </div>;
}
export default function AdvancedPropertyFilters() {
  const wp_query_params = {
    post_type: "property",
    per_page: 10,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="252638" id="advanced-property-filters" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-8 flex flex-col gap-2">
          <h2 className="text-foreground font-default text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
            Find Your Perfect Home
          </h2>
          <p className="text-muted-foreground font-sans text-lg max-w-2xl">
            Browse our exclusive marketplace of owner-financed properties. Funnel by down payment, monthly costs, and more to find your perfect match.
          </p>
        </div>
        
        <PostsProvider wp_query={{
        post_type: "property",
        per_page: 10,
        order: "desc",
        orderby: "date"
      }}>
          <QuestionBanksTable />
        </PostsProvider>
      </div>
    </section>;
}